<script setup>
import {getCurrentInstance, nextTick, onBeforeUnmount, reactive, ref, watch, onMounted} from 'vue'
import {Widget} from '@/paks/vu-widgets'
import {
    Feedback,
    State,
    allow,
    clone,
    delay,
    expand,
    expressionAsync,
    getModels,
    getValue,
    waitRender,
} from '@/paks/vu-app'
import GridLayout from './GridLayout'
import ExactLayout from './ExactLayout'
import Json5 from 'json5'

const {proxy: self} = getCurrentInstance()

const props = defineProps({
    //  TODO - rename board
    dashboard: Object,
    design: Boolean,
    id: String,
    layout: String,
    range: Object,
})

const page = reactive({
    canDoWidgets: null,
    grid: 4,
    message: null,
    pos: null,
    ready: false,
    updating: false,
    width: null,
    widgets: [],
    zoom: false,
    zoomedWidget: {},
    zoomReady: false,
})

const emit = defineEmits(['edit', 'remove', 'update'])
defineExpose({update})

const DefaultInputHeight = 80
const DefaultHeight = 200
const DefaultWidth = '.24'
const DefaultPeriods = [300, 3600, 86400, 604800, 2419200, 31536000]

let layout = null
let listeners = []

//  Group in a drag {}
let copy = null
let movingElement = null
let movingWidget = null
let startPos = null
let currentEl = null

const widgetRefs = ref(null)
const zoomedWidget = ref(null)
const {Metric} = getModels()

watch(() => page.zoom, zoomChanged)

onMounted(async () => {
    page.message = 'Add your first widget ...'
})

onBeforeUnmount(() => {
    removeListeners()
})

async function prepWidgets() {
    let dashboard = props.dashboard
    page.grid = dashboard.layout == 'grid' ? 20 : 4
    let widgets = []
    for (let widget of dashboard.widgets) {
        widgets.push(widget)
        prepWidget(widget)
    }
    page.widgets = widgets
    setFieldValues()
}

function prepWidget(widget) {
    try {
        let type = widget.type
        let prefix
        if (widget.cloudId) {
            let cloud = State.get('Cloud', widget.cloudId)
            if (cloud) {
                prefix = cloud.name
            } else if (widget.cloudId != 'builder' && State.cache.clouds.length == 1) {
                // if cloud recreated
                widget.cloudId = State.cache.clouds[0].id
            }
        }
        if (!prefix) {
            prefix = widget.namespace
        }
        let dimensions = ''
        if (dimensions) {
            //  Convert the dimensions into a string
            dimensions = widget.dimensions
                .map((d) => {
                    return d
                        .split(',')
                        .map((t) => t.split('=')[1])
                        .join(', ')
                })
                .join(', ')
            if (dimensions !== 'true') {
                if (widget.type == 'numeric') {
                    widget.footer = dimensions
                } else {
                    widget.footer = dimensions
                }
            }
        }
        if (type == 'numeric' || type == 'gauge') {
            widget.value = 0
        } else if (type == 'graph') {
            widget.value = {}
        }
        widget.width = widget.width || DefaultWidth
        if (widget.type == 'input') {
            if (widget.input == 'textarea') {
                widget.height = widget.height || DefaultHeight
            } else {
                widget.height = widget.height || DefaultInputHeight
            }
        } else if (widget.type == 'form') {
            widget.height = widget.height || DefaultInputHeight
        } else {
            widget.height = widget.height || DefaultHeight
        }
        if (widget.range?.override) {
            widget.range = widget.range
        } else {
            widget.range = props.range
        }
    } catch (err) {
        // continue
    }
}

/*
    Update widgets
    Compact will eliminate space between widgets
    Expand will grow widgets to fill horizontal view port
    Fetch is when there is new data (not used here)
    Initialize is for first time
    Layout is to recalc the layout
    Lazy return if already updating
 */
async function update(params = {expand: false, fetch: null, initialize: false, layout: true, lazy: false}) {
    while (page.updating || !State.auth.ready) {
        if (params.lazy) return 'updating'
        await delay(100)
    }
    page.updating = true
    params.range = props.range
    try {
        if (params.initialize) {
            await prepWidgets()
            await waitRender()
        }
        if (params.fetch !== false) {
            await fetchData()
        }
        if (params.layout !== false) {
            if (self.$el.clientWidth <= page.grid) {
                await waitRender()
            }
            page.width = self.$el.clientWidth
            if (params.layout !== false) {
                if (props.design && props.layout == 'grid') {
                    layout = new GridLayout({dash: props.dashboard, parent: self, vw: page.width})
                    page.widgets.forEach((w) => (w.z = 0))
                } else {
                    layout = new ExactLayout(self)
                }
                let widgets = await showWidgets()
                layout.run(widgets, params)
            }
        }
        page.ready = true
        await propagate(params)
        State.app.setNeed('widgetsRendered')
    } catch (err) {
        console.log('Widget update exception', {err})
    } finally {
        page.updating = false
    }
}

/*
    Set default field values for inputs widgets
*/
function setFieldValues() {
    for (let widget of page.widgets.filter((w) => w.type == 'input' && w.field)) {
        let key
        if (widget.model == 'Store' && widget.dimensions.key) {
            key = widget.dimensions.key
        } else {
            key = widget.field
        }
        if (!State.context.inputs[key]) {
            State.context.inputs[key] = widget.value || expand(widget.defaultValue)
        }
    }
}

/*
    Fetch data for widgets
 */
async function fetchData() {
    if (page.widgets == null) return
    let {items, widgets} = prepFetch(filterWidgets(page.widgets))
    if (!items.length) return
    let results
    try {
        results = await Metric.fetch({items})
    } catch (err) {}
    if (!results) {
        return
    }
    processResults(widgets, items, results)
}

function filterWidgets(widgets) {
    return widgets.filter((w) => {
        if (!(w.metric || w.model) || w.type == 'form' || w.type == 'toolbar') {
            return false
        }
        return true
    })
}

function prepFetch(list) {
    let items = []
    let widgets = []
    for (let widget of list) {
        delete widget._error
        widget.value = widget.value || widget.defaultValue

        let item = clone(
            allow(widget, [
                'accumulate',
                'cloudId',
                'dimensions',
                'field',
                'namespace',
                'metric',
                'model',
                'period',
                'statistic',
            ])
        )
        let range = widget.range?.override ? widget.range : props.range
        item.period = getValue(range.period || 86400)
        if (range.anchor == 'absolute') {
            item.start = getStart(range).getTime()
        } else if (DefaultPeriods.indexOf(item.period) < 0) {
            item.start = getStart(range).getTime()
        }
        if (widget.type == 'metric') {
            item.multiple = true
        }
        if (widget.metric) {
            item.accumulate =
                widget.type == 'info' || widget.type == 'timeline' || widget.type == 'graph' ? false : true
        }
        if (widget.model) {
            if (widget.form && widget.input != 'form') {
                /*
                   If part of a form and the form has the same data fetch, skip this widget as 
                   we can get the widget data from the form results
                   OPT - could generalize to not just forms but any two widgets with the same data fetch
                */
                let form = list.find((w) => w.form == widget.form && w.input == 'form')
                if (
                    form &&
                    widget.model == form.model &&
                    Json5.stringify(widget.dimensions) == Json5.stringify(form.dimensions)
                ) {
                    continue
                }
            }
            if (widget.type != 'table' && widget.type != 'event') {
                item.limit = 1
            }
            item.model = expand(item.model)
            item.field = expand(item.field)
            delete widget.metric
            delete widget.statistic
        } else if (widget.metric) {
            item.metric = expand(item.metric)
            delete widget.field
            if (item.metric.indexOf('${') >= 0) {
                //}
                widget._error = 'Could not expand metric token'
                continue
            }
        }
        item.statistic = expand(item.statistic)
        if (item.dimensions) {
            for (let [key, value] of Object.entries(item.dimensions)) {
                if (value.indexOf('${') >= 0) {
                    //}
                    value = expand(value)
                    if (value.indexOf('${') >= 0) {
                        //}
                        widget._error = 'Could not expand dimensions token'
                        continue
                    }
                    item.dimensions[key] = value
                }
            }
        }
        items.push(item)
        widgets.push(widget)
    }
    return {items, widgets}
}

function getProperty(result, path) {
    if (!path) {
        return result
    }
    for (let prop of path.split('.')) {
        if (result == null) {
            break
        }
        result = result[prop]
    }
    return result
}

function processResults(widgets, items, results) {
    let index = 0
    for (let widget of widgets) {
        let result = results[index++]
        if (!result) {
            continue
        }
        let item = result[0]
        if (item?.error) {
            if (widget.defaultValue) {
                widget.value = widget.value || widget.defaultValue
            }
            widget._error = item.error
            console.error(item.error, {widget, item})
            continue
        }
        if (widget.metric && widget.namespace != 'Database') {
            if (widget.type == 'metric') {
                widget.value = result || []
            } else {
                widget.value = result[0] || {}
            }
        } else if (widget.model) {
            if (widget.field && widget.type != 'table') {
                //  FUTURE -- extract the field server/side and receive back points[0] == value
                if (result.length == 1) {
                    widget.value = getProperty(item, widget.field)
                    if (widget.type == 'graph' || widget.type == 'gauge' || widget.type == 'numeric') {
                        if (widget.value == null) {
                            widget.value = widget.defaultValue
                        }
                        widget.value = widget.value - 0
                    }
                }
            } else {
                widget.value = result
            }
            if (widget.type == 'input') {
                if (widget.input == 'form') {
                    for (let w of props.dashboard.widgets) {
                        if (w.form == widget.form && w.input != 'form') {
                            let field = w.field || w.label?.toLowerCase()
                            if (field) {
                                w.value = getProperty(item, field)
                                if (w.value == null) {
                                    w.value = w.defaultValue
                                }
                                State.context.inputs[field] = w.value
                            }
                        }
                    }
                } else if (widget.field && !widget.form) {
                    // Solo input widgets
                    State.context.inputs[widget.field] = widget.value
                }
            }
        }
    }
}

/*
    Propagate data to the widgets by calling widget.update()
    We do this rather than using reactivity do each widget does not need to watch for changes
    If a function does not need it, it can use reactivity based on widget.value
 */
async function propagate(params) {
    //  Allow layout to render
    await waitRender()

    if (widgetRefs.value) {
        for (let wref of widgetRefs.value) {
            //  So that widgets (graphWidget) can access State
            wref.dark = State.app.dark
            if (wref.update && typeof wref.update == 'function') {
                await wref.update(params)
            }
        }
    }
}

async function showWidgets() {
    let widgets = []
    for (let widget of page.widgets) {
        if (await showWidget(widget)) {
            widgets.push(widget)
            widget._hide = false
        } else {
            widget._hide = true
        }
    }
    return widgets
}

async function showWidget(widget) {
    if (!widget.show) return true
    /*
        Store if the widget should be shown. If in design mode, the widget will be shown with "X"
     */
    let show = await expressionAsync(widget.show, {
        context: {value: widget.value, inputs: State.context.inputs},
    })
    if (props.design) {
        widget.shadow = !show
        return true
    }
    widget.shadow = false
    return show
}

function getStart(range) {
    if (range.anchor == 'absolute') {
        return new Date(range.start)
    }
    let start = new Date(Date.now() - range.period * 1000)
    return getAlignedDate(start)
}

function getAlignedDate(when) {
    when = new Date(when)
    return new Date(when.getFullYear(), when.getMonth(), when.getDate(), when.getHours(), when.getMinutes())
}

function findWidget(el) {
    let dash = document.getElementById('widgets')
    if (!dash) return {}
    while (el != el.parentElement) {
        let index = Array.prototype.indexOf.call(dash.children, el)
        if (index >= 0) {
            let widget = page.widgets.find((w) => w.id == el.id)
            return {element: el, widget}
        }
        el = el.parentElement
    }
    return {}
}

function createCopy(element) {
    let attach = document.getElementById('dash-attach')
    if (!attach) return null
    let cn = element.cloneNode(false)
    let height = element.offsetHeight || 40
    let width = element.offsetWidth || 300

    cn.style.top = element.offsetTop + 'px'
    cn.style.left = element.offsetLeft + 'px'
    cn.style.height = height + 'px'
    cn.style.width = width + 'px'
    cn.style.display = 'normal'
    cn.className += ' copy-widget'
    cn.id = 'copy-widget'
    attach.appendChild(cn)
    return cn
}

function startMove(e) {
    let {element, widget} = findWidget(e.target)
    if (!element || !widget || e.button == 2 || !props.design) {
        return
    }
    if (
        (widget.anchor?.horizontal && widget.anchor.horizontal != 'None') ||
        (widget.anchor?.vertical && widget.anchor.vertical != 'None')
    ) {
        Feedback.error('You cannot move an anchored widget')
        return
    }
    if (props.dashboard.layout == 'exact') {
        let maxZ = Math.max(...props.dashboard.widgets.filter((w) => w.type != 'toolbar').map((w) => w.z))
        widget.z = maxZ + 1
    }
    if (e.metaKey) {
        return openZoom(widget)
    }
    if (e.altKey) {
        State.app.setNeed('editWidget', widget)
        return
    }
    if (widget.type == 'toolbar') {
        if (e.button == 2) {
            return
        }
        if (e.target.classList.toString().indexOf('toolbar') < 0) {
            return
        }
    }
    /*
        Create a copied element to move via the mouse
        movingWidget and movingElement are the original widget/element. These will be placed by the layout mgr.
    */
    copy = createCopy(element)
    movingElement = element
    movingWidget = widget
    element.className += ' moving-widget'

    widget.moving = true
    startPos = page.pos = getWidgetPos(e, copy)
    startPos.left = align(startPos.left, page.grid, 'round')
    startPos.top = align(startPos.top, page.grid, 'round')

    setProposed(widget)

    addListener(document, 'mousemove', move)
    addListener(document, 'mouseup', finishMove)
    addListener(document, 'touchmove', move)
    addListener(document, 'touchend', finishMove)
}

async function move(e) {
    if (!copy) return
    let pos = (page.pos = getWidgetPos(e, copy))
    let left = pos.x - startPos.x + startPos.left
    let top = pos.y - startPos.y + startPos.top

    copy.style.top = top + 'px'
    copy.style.left = left + 'px'
    copy.style.cursor = 'move'
    let widget = movingWidget

    let deltaX = pos.x - startPos.x
    let deltaY = pos.y - startPos.y

    if (e?.touches?.length) {
        currentEl = document.elementFromPoint(e.touches[0].clientX, e.touches[0].clientY)
    } else {
        currentEl = document.elementFromPoint(e.clientX, e.clientY)
    }
    setProposed(widget, deltaX, deltaY)

    nextTick(async () => {
        //  This will move the widget as to a proposed layout position
        await layout.run(page.widgets)
    })
}

async function finishMove(e) {
    if (!copy) {
        return
    }
    let pos = getWidgetPos(e, copy)
    let deltaX = pos.x - startPos.x
    let deltaY = pos.y - startPos.y

    let widget = movingWidget
    setProposed(widget, deltaX, deltaY)

    movingElement.style.top = widget.top + 'px'
    movingElement.style.left = widget.left * 100 + '%'
    movingElement.className = movingElement.className.replace(/ moving-widget/, '')

    let attach = document.getElementById('dash-attach')

    removeListeners(['mousemove', 'mouseup', 'touchmove', 'touchend'])

    movingWidget = null
    movingElement = null

    if (deltaX || deltaY) {
        await layout.run(page.widgets, {layout: true})
    }
    delete widget.moving
    delete widget.prior
    delete widget.proposed

    attach.removeChild(copy)
    copy = null

    if (deltaX || deltaY) {
        emit('update', page.widgets)
        widget._moved = new Date()
    }
    if (currentEl && typeof currentEl.click == 'function') {
        currentEl.click()
    }
}

function setProposed(widget, deltaX = 0, deltaY = 0) {
    widget.proposed = {
        _top: Math.max(align(startPos.top + deltaY, page.grid, 'round'), 0),
        _left: Math.max(align(startPos.left + deltaX, page.grid, 'round'), 0),
    }
}

async function resizeWidget(params) {
    let cmd = params.cmd
    let widget = page.widgets.find((w) => w.id == params.id)
    if (!widget) return
    if (cmd == 'start-resize') {
    } else if (cmd == 'resize') {
    } else if (cmd == 'finish-resize') {
        widget.width = Math.min(params.width / page.width, 1)
        widget.height = params.height
        emit('update', page.widgets)
    }
}

function getWidgetPos(event, el) {
    let dash = document.getElementById('widgets')
    let dashRect = dash.getBoundingClientRect()
    let rect = el.getBoundingClientRect()
    let left = rect.left - dashRect.left - 2
    let top = rect.top - dashRect.top
    let width, height, x, y
    if (event.touches) {
        if (event.touches.length) {
            x = event.touches[0].clientX
            y = event.touches[0].clientY
        } else {
            x = page.pos.x
            y = page.pos.y
        }
    } else {
        x = event.clientX
        y = event.clientY
    }
    width = x - rect.left
    height = y - rect.top
    return {height, width, left, top, rect, x, y}
}

async function addWidget(widget) {
    emit('edit')
}

async function editWidget(widget) {
    emit('edit', widget)
}

async function removeWidget(params) {
    let widget = page.widgets.find((w) => w.id == params.id)
    if (widget) {
        emit('remove', widget)
    }
}

async function openZoom(widget) {
    if (widget.type == 'input') {
        return
    }
    widget = page.zoomedWidget = clone(widget)
    widget.height = 1.0
    widget.width = 1.0
    widget.z = 1
    widget.zoomed = 'zoomed'
    page.zoom = true
    page.zoomReady = true
    for (let i = 0; i < 50; i++) {
        await waitRender()
        await delay(10)
        if (zoomedWidget.value.update && typeof zoomedWidget.value.update == 'function') {
            await zoomedWidget.value.update()
            break
        }
    }
}

async function closeZoom() {
    page.zoom = false
    page.zoomReady = false
    page.zoomedWidget = {}
}

function zoomChanged(visible) {
    if (!visible) {
        closeZoom()
    }
}

function addListener(base, event, fn) {
    listeners.push({base, event, fn})
    base.addEventListener(event, fn)
}

function removeListeners(events) {
    for (let listener of listeners) {
        if (!events || events.indexOf(listener.event) >= 0) {
            listener.base.removeEventListener(listener.event, listener.fn)
        }
    }
    listeners = []
}

function align(v, round = page.grid, dir = 'up') {
    if (round && props.dashboard.layout == 'grid') {
        if (dir == 'down') {
            v = Math.floor(v / round) * round
        } else if (dir == 'up') {
            v = Math.ceil(v / round) * round
        } else {
            v = Math.round(v / round) * round
        }
    }
    return parseInt(v) - 0
}
</script>

<template>
    <div class="widgets" id="widgets">
        <Widget
            v-show="page.ready"
            v-for="widget of page.widgets"
            :dashboard="props.dashboard"
            :design="props.design"
            :key="widget.id"
            :id="widget.id"
            :value="widget.value"
            :widget="widget"
            ref="widgetRefs"
            @resize="resizeWidget"
            @edit="editWidget"
            @remove="removeWidget"
            @mousedown="startMove"
            @touchstart="startMove"
            @dblclick.stop="openZoom(widget)" />

        <div id="dash-attach" />

        <div v-if="page.widgets.length == 0 && props.dashboard?.type == 'dashboard'">
            <h2 class="no-widgets" v-if="page.widgets.length == 0" @click="addWidget">
                {{ page.message }}
            </h2>
        </div>

        <v-dialog v-model="page.zoomReady" overlay-opacity="50%" height="100%" width="100%" content-class="zoom">
            <v-container>
                <Widget
                    v-if="page.zoomReady"
                    class="zoomed"
                    ref="zoomedWidget"
                    :dashboard="props.dashboard"
                    :design="props.design"
                    :id="page.zoomedWidget.id"
                    :value="page.zoomedWidget.value"
                    :widget="page.zoomedWidget"
                    @remove="closeZoom">
                    <template v-slot:widgets="widget"></template>
                </Widget>
            </v-container>
        </v-dialog>
    </div>
</template>

<style lang="scss">
.widgets {
    height: 100%;
    width: 100%;
    min-width: 20px;
    margin: 0;
    padding: 0;
    display: block;
    position: relative;
    padding-bottom: 40px;
    .widget {
        position: absolute;
        height: initial;
    }
    .copy-widget {
        border: 2px solid rgb(var(--v-theme-border-darken-2)) !important;
        border: 2px solid #222222 !important;
        border-radius: 8px;
        background-color: rgb(var(--v-secondary)) !important;
        opacity: 0.75;
        z-index: 10 !important;
    }
    .moving-widget {
        opacity: 0.75;
        z-index: 5 !important;
    }
    .no-widgets {
        padding: 0;
        font-size: 1.5rem;
        color: rgb(var(--v-theme-text)) !important;
    }
}
.zoom {
    height: 100%;
    width: 100%;
    margin: 0 !important;
    max-height: 100% !important;
    max-width: 100% !important;
    border-radius: 0 !important;
    height: 100%;
    background: rgb(var(--v-theme-surface-lighten-3));
    .v-container {
        height: 100% !important;
        width: 100% !important;
        padding: 0 !important;
        max-width: 100% !important;
        display: block;
        .widget {
            height: 100%;
            width: 100%;
        }
        .zoomed {
            height: 100%;
            width: 100%;
        }
    }
}
</style>
